import { useId, useRef } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, ReactElement, ReactNode } from 'react';
import { cn } from './cn';

export interface TabItem<TId extends string> {
  id: TId;
  label: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
  /** When present, `Tabs` also renders the associated `tabpanel`. */
  content?: ReactNode;
}

export interface TabsProps<TId extends string> {
  items: readonly TabItem<TId>[];
  value: TId;
  onValueChange: (value: TId) => void;
  /** Accessible name for the tab list. */
  label: string;
  orientation?: 'horizontal' | 'vertical';
  /** Tabs share the available width equally. */
  fitted?: boolean;
  className?: string;
  listClassName?: string;
  panelClassName?: string;
}

/**
 * WAI-ARIA tabs with automatic activation: arrow keys move focus *and*
 * selection, which is the recommended pattern when switching panels is cheap.
 */
export function Tabs<TId extends string>({
  items,
  value,
  onValueChange,
  label,
  orientation = 'horizontal',
  fitted = false,
  className,
  listClassName,
  panelClassName,
}: TabsProps<TId>): ReactElement {
  const base = useId();
  const buttonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const vertical = orientation === 'vertical';
  const hasPanels = items.some((item) => item.content !== undefined);

  const tabDomId = (id: TId): string => `${base}tab-${id}`;
  const panelDomId = (id: TId): string => `${base}panel-${id}`;

  function activate(id: TId): void {
    if (id !== value) onValueChange(id);
    buttonRefs.current[id]?.focus({ preventScroll: true });
  }

  function step(from: number, delta: number): void {
    const count = items.length;
    if (count === 0) return;
    let index = from;
    for (let attempt = 0; attempt < count; attempt += 1) {
      index = (index + delta + count) % count;
      const candidate = items[index];
      if (!candidate.disabled) {
        activate(candidate.id);
        return;
      }
    }
  }

  function jump(edge: 'first' | 'last'): void {
    const ordered = edge === 'first' ? items : [...items].reverse();
    const target = ordered.find((item) => !item.disabled);
    if (target) activate(target.id);
  }

  function handleKeyDown(event: ReactKeyboardEvent<HTMLButtonElement>, index: number): void {
    const next = vertical ? 'ArrowDown' : 'ArrowRight';
    const previous = vertical ? 'ArrowUp' : 'ArrowLeft';

    if (event.key === next) {
      event.preventDefault();
      step(index, 1);
      return;
    }
    if (event.key === previous) {
      event.preventDefault();
      step(index, -1);
      return;
    }
    if (event.key === 'Home') {
      event.preventDefault();
      jump('first');
      return;
    }
    if (event.key === 'End') {
      event.preventDefault();
      jump('last');
    }
  }

  return (
    <div className={cn(vertical ? 'flex gap-4' : 'flex flex-col', className)}>
      <div
        role="tablist"
        aria-label={label}
        aria-orientation={orientation}
        className={cn(
          'flex',
          vertical
            ? 'shrink-0 flex-col gap-1 border-r border-border pr-2'
            : 'items-stretch gap-1 border-b border-border',
          listClassName,
        )}
      >
        {items.map((item, index) => {
          const selected = item.id === value;
          return (
            <button
              key={item.id}
              type="button"
              role="tab"
              id={tabDomId(item.id)}
              ref={(node) => {
                buttonRefs.current[item.id] = node;
              }}
              aria-selected={selected}
              aria-controls={item.content !== undefined ? panelDomId(item.id) : undefined}
              aria-disabled={item.disabled || undefined}
              tabIndex={selected ? 0 : -1}
              onClick={() => {
                if (item.disabled) return;
                activate(item.id);
              }}
              onKeyDown={(event) => handleKeyDown(event, index)}
              className={cn(
                'inline-flex select-none items-center justify-center gap-1.5 whitespace-nowrap',
                'text-[13px] font-medium transition-colors duration-150 motion-reduce:transition-none',
                'max-md:min-h-11',
                vertical
                  ? 'min-h-9 rounded-md px-3 py-2 text-left'
                  : 'min-h-9 rounded-t-md border-b-2 px-3 py-2',
                fitted && !vertical && 'flex-1',
                item.disabled && 'cursor-not-allowed opacity-50',
                selected
                  ? vertical
                    ? 'bg-accent-soft text-accent'
                    : 'border-accent text-ink'
                  : cn(
                      'text-ink-muted',
                      !vertical && 'border-transparent',
                      !item.disabled && 'hover:text-ink',
                    ),
              )}
            >
              {item.icon && (
                <span aria-hidden="true" className="flex [&>svg]:h-4 [&>svg]:w-4">
                  {item.icon}
                </span>
              )}
              {item.label}
            </button>
          );
        })}
      </div>

      {hasPanels && (
        <div className={cn('min-w-0 flex-1', panelClassName)}>
          {items.map((item) =>
            item.content === undefined ? null : (
              <div
                key={item.id}
                role="tabpanel"
                id={panelDomId(item.id)}
                aria-labelledby={tabDomId(item.id)}
                tabIndex={0}
                hidden={item.id !== value}
                className="outline-none"
              >
                {item.content}
              </div>
            ),
          )}
        </div>
      )}
    </div>
  );
}
